<template>
    <div>
        <div v-if="loading" class="text-center" style="padding: 40px 0;">
            <font-awesome-icon :icon="['fas', 'spinner']" spin size="2x" />
        </div>
        <div v-else>
            <div class="row">
                <div class="col-md-5">
                    <div class="card w-100 margin-botton-20 border-0" style="width: 18rem;">
                        <div class="card-body border-primary">
                            <h3 class="h5 card-title">Sun</h3>
                            <p class="card-text"><SunGraph :altitude="sunAltitudeAzimuth.altitude" :azimuth="sunAltitudeAzimuth.azimuth" /></p>
                            <p class="card-text">
                                <span v-if="sunAltitudeAzimuth.is_visible" class="badge bg-success text-white">Visible</span>
                                <span v-if="!sunAltitudeAzimuth.is_visible" class="badge bg-secondary text-white">Not visible</span>
                            </p>
                        </div>
                    </div>
                </div>
                <div class="col-md-7">
                    <div class="row margin-botton-20" style="margin-top: 20px;">
                        <div class="col-md-12">
                            <div class="row themed">
                                <div class="col-md-6">Sun Direction:</div>
                                <div class="col-md-6">
                                    {{ sunAltitudeAzimuth.formatted_azimuth }}
                                    <span :style="rotateArrow(sunAltitudeAzimuth.azimuth)">{{ arrow }}</span>
                                </div>
                            </div>
                            <div class="row themed">
                                <div class="col-md-6">Sun Altitude:</div>
                                <div class="col-md-6">{{ sunAltitudeAzimuth.altitude }}&deg;</div>
                            </div>
                            <div class="row themed">
                                <div class="col-md-6">Sun Distance:</div>
                                <div class="col-md-6">{{ sunDistance.formatted_distance_km }} Km</div>
                            </div>
                            <div class="row themed">
                                <div class="col-md-6">When:</div>
                                <div class="col-md-6">
                                    <span v-if="useNoon">Noon</span><a v-else href="#" @click.prevent="setNoon">Noon</a>
                                    &nbsp;/&nbsp;
                                    <a v-if="useNoon" href="#" @click.prevent="setNow">Now</a><span v-else>Now</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col-md-6" v-for="event in sunrisesSunsets" :key="event.id">
                    <div class="card w-100 margin-botton-20" style="width: 18rem;">
                        <div class="card-body event-card-bg">
                            <h3 class="h5 card-title">{{ event.name }}</h3>
                            <p class="symbol-display">{{ event.symbol }}</p>
                            <p class="card-text">{{ event.naturalday }} at {{ event.time }}</p>
                            <p class="card-text small">{{ event.naturaltime }}</p>
                            <p class="card-text small" v-if="event.name === 'Sunrise'">Day duration: {{ event.duration_hours }} hours</p>
                            <p class="card-text small" v-if="event.name === 'Sunset'">Night duration: {{ event.duration_hours }} hours</p>
                        </div>
                    </div>
                </div>
            </div>
            <div class="row mt-3" v-if="sunNoonAltitude !== null">
                <div class="col-12">
                    <div class="card w-100 margin-botton-20">
                        <div class="card-body event-card-bg">
                            <SunAltitudeChart
                                :sunrise="(sunrisesSunsets.find(e => e.name === 'Sunrise') || {}).time || ''"
                                :sunset="(sunrisesSunsets.find(e => e.name === 'Sunset') || {}).time || ''"
                                :noon-alt="sunNoonAltitude"
                                :current-time="useNoon ? null : time"
                                title="Sunrise &amp; Sunset"
                                :subtitle="chartSubtitle"
                                :subtitle2="chartSubtitle2"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div class="row mt-3" v-if="sunriseMonthlyRows.length">
                <div class="col-12">
                    <div class="card w-100 margin-botton-20">
                        <div class="card-body event-card-bg">
                            <h3 class="h4 card-title mb-3">{{ currentMonthLabel }}</h3>
                            <div class="table-responsive">
                            <table class="table table-sm mb-0">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Sunrise</th>
                                        <th>Sunset</th>
                                        <th>Duration</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="row in sunriseMonthlyRows" :key="row.date"
                                        :class="{ 'table-success': date && parseInt(row.date) === date.getDate() }"
                                        :style="date && parseInt(row.date) === date.getDate() ? { '--bs-table-bg': 'rgba(25,135,84,0.15)', '--bs-table-border-color': 'transparent' } : {}">
                                        <td>{{ row.date }}</td>
                                        <td>{{ row.sunrise }}</td>
                                        <td>{{ row.sunset }}</td>
                                        <td>{{ row.duration }} hrs</td>
                                    </tr>
                                </tbody>
                            </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import astroTabMixin from '@/mixins/astroTab'
import astra, { BODY } from '@/api/astra'
import SunGraph from './SunGraph'
import SunAltitudeChart from './SunAltitudeChart'

export default {
    name: 'TabSunriseSunset',
    mixins: [astroTabMixin],
    components: { SunGraph, SunAltitudeChart },
    props: {
        active: { type: Boolean, default: false },
    },
    data() {
        return {
            loading: true,
            sunrisesSunsets: [],
            sunrisesMonthly: [],
            sunNoonAltitude: null,
            sunAltitudeAzimuth: {},
            sunDistance: {},
            arrow: '↑',
        }
    },
    computed: {
        chartSubtitle() {
            if (!this.city || !this.city.name) return ''
            const parts = [this.city.name]
            if (this.city.region_name) parts.push(this.city.region_name)
            return 'near ' + parts.join(', ')
        },
        chartSubtitle2() {
            if (!this.date) return ''
            return 'for ' + this.date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
        },
        sunriseMonthlyRows() {
            const rows = []
            const events = this.sunrisesMonthly
            for (let i = 0; i < events.length - 1; i += 2) {
                const rise = events[i].name === 'Sunrise' ? events[i] : events[i + 1]
                const set  = events[i].name === 'Sunset'  ? events[i] : events[i + 1]
                rows.push({ date: rise.short_date.split('-')[2], sunrise: rise.time, sunset: set.time, duration: rise.duration_hours })
            }
            return rows
        },
        sunPathDateLabel() {
            if (!this.date) return "Today's Sun Path"
            return 'Sun Path — ' + this.date.toLocaleString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
        },
    },
    watch: {
        active(val) { if (val) this.load() },
        city()      { if (this.active) this.load() },
        date()      { if (this.active) this.load() },
    },
    mounted() {
        if (this.active) this.load()
    },
    methods: {
        async load() {
            if (!this.latitude || !this.longitude || !this.timezone) return
            if (this.apiLocked) return
            this.loading = true
            this.sunNoonAltitude = null
            const dt = this.dailyTimeRange.date1
            await Promise.allSettled([
                astra.fetchSunRiseSet({ ...this.loc, ...this.dailyRange }).then(r => { this.sunrisesSunsets = r.data.events }),
                astra.fetchSunRiseSet({ ...this.loc, ...this.monthlyRange }).then(r => { this.sunrisesMonthly = r.data.events }),
                astra.fetchAltitudeAzimuth({ ...this.loc, date1: dt, body: BODY.SUN }).then(r => {
                    this.sunAltitudeAzimuth = r.data
                    this.sunNoonAltitude = r.data.altitude
                }),
                astra.fetchBodyDistance({ ...this.loc, date1: dt, start: BODY.EARTH, end: BODY.SUN }).then(r => { this.sunDistance = r.data }),
            ])
            this.loading = false
        },
    },
}
</script>

<style scoped>
.themed {
    margin: 5px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.125);
    padding-bottom: 10px;
    padding-top: 10px;
}
.margin-botton-20 { margin-bottom: 20px; }
.symbol-display {
    font-size: 2.5rem;
    margin: 0;
    padding: 0;
    line-height: 1.2;
}
.table-responsive td,
.table-responsive th { white-space: nowrap; }
</style>
